import React, { memo, useMemo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Users, EyeOff, Eye, AlertCircle, Edit2, X, Save } from 'lucide-react';

const LLM_MODELS = [
  { value: 'gpt-4-0125-preview', label: 'GPT-4 Turbo (Preview)', provider: 'openai' },
  { value: 'gpt-4', label: 'GPT-4', provider: 'openai' },
  { value: 'gpt-3.5-turbo-0125', label: 'GPT-3.5 Turbo', provider: 'openai' }
];

interface AgentNodeProps {
  id: string;
  data: {
    label: string;
    role?: string;
    goals?: string;
    backstory?: string;
    llmModel?: string;
    apiKey?: string;
    allowDelegation?: string;
    memory?: string;
    onPropertyChange?: (property: string, value: any) => void;
    onLabelChange?: (label: string) => void;
    selected?: boolean;
  };
  selected: boolean;
}

export const AgentNode = memo(({ id, data, selected }: AgentNodeProps) => {
  const [state, setState] = React.useState({
    showApiKey: false,
    showHelp: false,
    isEditing: false,
    originalValues: {} as Record<string, any>,
    dirtyFields: new Set<string>()
  });

  const handleStartEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setState(prev => ({
      ...prev,
      isEditing: true,
      originalValues: {
        role: data.role || '',
        goals: data.goals || '',
        backstory: data.backstory || '',
        llmModel: data.llmModel || 'gpt-4-0125-preview',
        apiKey: data.apiKey || '',
        allowDelegation: data.allowDelegation || 'false',
        memory: data.memory || ''
      }
    }));
  }, [data]);

  const handleSave = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.onPropertyChange) {
      Object.entries(state.originalValues).forEach(([key, value]) => {
        if (state.dirtyFields.has(key)) {
          data.onPropertyChange(key, value);
        }
      });
    }
    setState(prev => ({
      ...prev,
      isEditing: false,
      originalValues: {},
      dirtyFields: new Set()
    }));
  }, [state, data.onPropertyChange]);

  const handleCancel = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setState(prev => ({
      ...prev,
      isEditing: false,
      originalValues: {},
      dirtyFields: new Set()
    }));
  }, []);

  const handleFieldChange = useCallback((field: string, value: any) => {
    setState(prev => ({
      ...prev,
      dirtyFields: new Set([...prev.dirtyFields, field]),
      originalValues: {
        ...prev.originalValues,
        [field]: value
      }
    }));
  }, []);

  const handleLabelChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (data.onLabelChange) {
      data.onLabelChange(e.target.value);
    }
  }, [data.onLabelChange]);

  const toggleApiKey = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setState(prev => ({ ...prev, showApiKey: !prev.showApiKey }));
  }, []);

  const toggleHelp = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setState(prev => ({ ...prev, showHelp: !prev.showHelp }));
  }, []);

  const baseInputClass = [
    'w-full px-3 py-2 text-sm bg-gray-700/50 border rounded-none',
    'focus:ring-0 focus:border-indigo-500',
    'transition-all duration-200',
    state.isEditing ? 'border-indigo-500' : 'border-transparent'
  ].join(' ');

  const nodeClass = [
    'group relative bg-gray-800/90 backdrop-blur-sm transition-all duration-200',
    selected ? 'ring-2 ring-indigo-500 ring-inset shadow-lg shadow-indigo-500/20' : 'ring-1 ring-gray-700/50 ring-inset'
  ].join(' ');

  return (
    <div 
      className={nodeClass}
      style={{ cursor: state.isEditing ? 'default' : 'move' }}
    >
      <Handle 
        type="target" 
        position={Position.Top} 
        className="w-2 h-2 !bg-blue-500 -top-1 border-none" 
      />

      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-700/50">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Users className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <input
            className="flex-1 min-w-0 bg-transparent border-none font-medium text-gray-100 focus:ring-0 focus:outline-none placeholder-gray-500"
            value={data.label || ''}
            onChange={handleLabelChange}
            placeholder="Agent Name"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        {!state.isEditing ? (
          <button
            onClick={handleStartEdit}
            className="p-1 -m-1 hover:bg-gray-700/50 rounded-full text-gray-400 hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-all"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <button
              onClick={handleCancel}
              className="p-1 -m-1 hover:bg-gray-700/50 rounded-full text-gray-400 hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
            <button
              onClick={handleSave}
              className="p-1 -m-1 hover:bg-gray-700/50 rounded-full text-gray-400 hover:text-gray-300"
            >
              <Save className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Role Badge */}
      {!state.isEditing && data.role && (
        <div className="px-3 py-2 text-sm text-gray-400 border-b border-gray-700/50">
          {data.role}
        </div>
      )}

      {/* Expanded Content */}
      {state.isEditing && (
        <div className="divide-y divide-gray-700/50" onClick={(e) => e.stopPropagation()}>
          <div className="p-3 space-y-1">
            <label className="block text-xs font-medium text-gray-400">Role</label>
            <input
              className={baseInputClass}
              placeholder="e.g., Researcher, Writer, Analyst"
              value={state.originalValues.role || ''}
              onChange={(e) => handleFieldChange('role', e.target.value)}
            />
          </div>

          <div className="p-3 space-y-1">
            <label className="block text-xs font-medium text-gray-400">Goals</label>
            <textarea
              className={baseInputClass}
              placeholder="One goal per line"
              rows={3}
              value={state.originalValues.goals || ''}
              onChange={(e) => handleFieldChange('goals', e.target.value)}
            />
          </div>

          <div className="p-3 space-y-1">
            <label className="block text-xs font-medium text-gray-400">Backstory</label>
            <textarea
              className={baseInputClass}
              placeholder="Optional personality and background"
              rows={2}
              value={state.originalValues.backstory || ''}
              onChange={(e) => handleFieldChange('backstory', e.target.value)}
            />
          </div>

          <div className="p-3 space-y-1">
            <label className="block text-xs font-medium text-gray-400">LLM Model</label>
            <select
              className={baseInputClass}
              value={state.originalValues.llmModel || 'gpt-4-0125-preview'}
              onChange={(e) => handleFieldChange('llmModel', e.target.value)}
            >
              {LLM_MODELS.map(model => (
                <option key={model.value} value={model.value}>{model.label}</option>
              ))}
            </select>
          </div>

          <div className="p-3 space-y-1">
            <label className="block text-xs font-medium text-gray-400">API Key</label>
            <div className="relative">
              <input
                type={state.showApiKey ? "text" : "password"}
                className={`${baseInputClass} pr-8`}
                placeholder="OpenAI API Key"
                value={state.originalValues.apiKey || ''}
                onChange={(e) => handleFieldChange('apiKey', e.target.value)}
              />
              <button
                type="button"
                onClick={toggleApiKey}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
              >
                {state.showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="p-3 space-y-1">
            <label className="block text-xs font-medium text-gray-400">Delegation</label>
            <select
              className={baseInputClass}
              value={state.originalValues.allowDelegation || 'false'}
              onChange={(e) => handleFieldChange('allowDelegation', e.target.value)}
            >
              <option value="true">Allow Delegation</option>
              <option value="false">Disable Delegation</option>
            </select>
          </div>

          <div className="p-3 space-y-1">
            <label className="block text-xs font-medium text-gray-400">Memory (tokens)</label>
            <input
              type="number"
              className={baseInputClass}
              placeholder="Memory tokens"
              value={state.originalValues.memory || ''}
              onChange={(e) => handleFieldChange('memory', e.target.value)}
              min="0"
            />
          </div>
        </div>
      )}

      <Handle 
        type="source" 
        position={Position.Bottom} 
        className="w-2 h-2 !bg-blue-500 -bottom-1 border-none" 
      />
    </div>
  );
});

AgentNode.displayName = 'AgentNode';
